import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatCurrency } from '../../utils/formatters';
import { reconEngine } from '../../services/reconciliationEngine';
import { reconciliationService } from '../../services/reconciliationService';

const { 
  FiX, FiShield, FiCheckCircle, FiAlertCircle, 
  FiArrowLeft, FiChevronRight, FiInfo, FiCalendar,
  FiDatabase, FiLock, FiLoader
} = FiIcons;

export default function ReconciliationWizard({ isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  const [setup, setSetup] = useState({
    accountId: '',
    endDate: new Date().toISOString().split('T')[0],
    startBalance: 0,
    endBalance: 0
  });

  const [clearedIds, setClearedIds] = useState(new Set());
  
  const activeAccount = accounts.find(a => a.id === setup.accountId);
  const accountType = activeAccount?.account_type === 'credit_card' ? 'liabilities' : 'assets';

  useEffect(() => {
    if (isOpen) {
      loadAccounts();
      setStep(1);
      setError(null);
      setClearedIds(new Set());
    }
  }, [isOpen]);

  const loadAccounts = async () => {
    try {
      const data = await reconciliationService.getBankAccounts();
      setAccounts(data);
      if (data.length > 0) {
        setSetup(prev => ({ ...prev, accountId: data[0].id }));
      }
    } catch (err) {
      setError("Failed to load bank accounts.");
    }
  };

  useEffect(() => {
    if (setup.accountId) {
      fetchLastRecon();
    }
  }, [setup.accountId]);

  const fetchLastRecon = async () => {
    try {
      const last = await reconciliationService.getLastReconciliation(setup.accountId);
      setSetup(prev => ({ 
        ...prev, 
        startBalance: last?.ending_balance || activeAccount?.balance || 0 
      }));
    } catch (err) {
      console.error(err);
    }
  };

  const startMatching = async () => {
    if (!setup.accountId) return;
    setLoading(true);
    setError(null);
    try {
      const txs = await reconciliationService.getUnreconciledTransactions(setup.accountId, setup.endDate);
      setTransactions(txs);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const clearedBalance = useMemo(() => {
    const selectedTxs = transactions.filter(tx => clearedIds.has(tx.id));
    return reconEngine.calculateClearedBalance(setup.startBalance, selectedTxs, accountType);
  }, [setup.startBalance, transactions, clearedIds, accountType]);

  const difference = useMemo(() => {
    return parseFloat((setup.endBalance - clearedBalance).toFixed(2));
  }, [setup.endBalance, clearedBalance]);

  const toggleClear = (id) => {
    const newSet = new Set(clearedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setClearedIds(newSet);
  };

  const handleFinish = async () => {
    if (difference !== 0) return;
    setLoading(true);
    setError(null);
    try {
      await reconciliationService.completeReconciliation({
        bank_connection_id: setup.accountId,
        statement_date: setup.endDate,
        opening_balance: setup.startBalance,
        ending_balance: setup.endBalance,
        clearedIds: Array.from(clearedIds)
      });
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 backdrop-blur-md p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-[#f8fafc] w-full max-w-7xl h-[92vh] rounded-[40px] shadow-2xl overflow-hidden flex flex-col border border-white/20"
      >
        {/* Step Header */}
        <div className="px-10 py-6 border-b border-slate-200 bg-white flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg ${step === 1 ? 'bg-blue-600 text-white' : 'bg-emerald-500 text-white'}`}>
              <SafeIcon icon={FiShield} className="text-xl" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">
                Reconcile {activeAccount?.account_name || 'Account'}
              </h2>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
                {step === 1 ? 'Step 1: Statement Configuration' : 'Step 2: Transaction Matching'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-slate-100 rounded-full text-slate-400 transition-colors">
            <SafeIcon icon={FiX} className="text-2xl" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {step === 1 ? (
            /* STEP 1: SETUP UI */
            <div className="flex-1 flex flex-col items-center justify-center p-12 bg-white overflow-y-auto">
              <div className="w-full max-w-2xl space-y-10 py-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiCalendar} className="text-3xl text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 mb-2">Enter Statement Details</h3>
                  <p className="text-slate-500 font-medium italic">Pull these values directly from your monthly bank statement.</p>
                </div>

                {error && (
                  <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-sm font-medium flex items-start gap-3">
                    <SafeIcon icon={FiAlertCircle} className="text-lg flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-8">
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Target Account</label>
                    <select 
                      value={setup.accountId} 
                      onChange={(e) => setSetup({...setup, accountId: e.target.value})}
                      className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-blue-500 transition-all"
                    >
                      <option value="" disabled>Select Account</option>
                      {accounts.map(acc => (
                        <option key={acc.id} value={acc.id}>{acc.account_name} (...{acc.mask})</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Statement End Date</label>
                    <input 
                      type="date" 
                      value={setup.endDate}
                      onChange={(e) => setSetup({...setup, endDate: e.target.value})}
                      className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none" 
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-8">
                  <div className="p-6 bg-slate-50 rounded-[32px] border border-slate-100">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Opening Balance</p>
                    <p className="text-2xl font-black text-slate-900">{formatCurrency(setup.startBalance)}</p>
                    <p className="text-[10px] text-emerald-600 font-bold mt-1">From last period</p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-blue-600 uppercase tracking-widest">Statement Ending Balance</label>
                    <div className="relative">
                      <span className="absolute left-5 top-1/2 -translate-y-1/2 font-black text-slate-400">$</span>
                      <input 
                        type="number" 
                        step="0.01"
                        placeholder="0.00"
                        value={setup.endBalance || ''}
                        onChange={(e) => setSetup({...setup, endBalance: parseFloat(e.target.value) || 0})}
                        className="w-full pl-10 pr-5 py-6 bg-blue-50/30 border-2 border-blue-100 rounded-[32px] text-2xl font-black outline-none focus:border-blue-500 transition-all" 
                      />
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-amber-50 border border-amber-100 rounded-2xl flex items-start gap-4">
                  <SafeIcon icon={FiInfo} className="text-amber-600 mt-1" />
                  <p className="text-xs text-amber-800 leading-relaxed font-medium">
                    <strong>Audit Tip</strong>: If your opening balance doesn't match your statement, check for deleted or modified transactions in previously reconciled periods.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* STEP 2: WORKBENCH UI */
            <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
              {/* Left: Transaction Ledger */}
              <div className="flex-1 flex flex-col border-r border-slate-200 bg-white">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                  <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">Unreconciled Transactions</h3>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 text-[10px] font-black rounded-full uppercase">
                      {clearedIds.size} Selected
                    </span>
                  </div>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                  {transactions.length === 0 ? (
                    <div className="p-20 text-center flex flex-col items-center">
                      <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                        <SafeIcon icon={FiCheckCircle} className="text-2xl text-slate-300" />
                      </div>
                      <p className="font-bold text-slate-900">No transactions to match</p>
                      <p className="text-sm text-slate-500">All transactions for this period are already reconciled.</p>
                    </div>
                  ) : (
                    <table className="w-full text-left">
                      <thead className="sticky top-0 bg-white shadow-sm z-10">
                        <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          <th className="py-4 px-8 w-16">Clear</th>
                          <th className="py-4 px-4 w-32">Date</th>
                          <th className="py-4 px-4">Description / Payee</th>
                          <th className="py-4 px-8 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {transactions.map(tx => (
                          <tr 
                            key={tx.id} 
                            onClick={() => toggleClear(tx.id)}
                            className={`group cursor-pointer transition-colors ${clearedIds.has(tx.id) ? 'bg-emerald-50/40' : 'hover:bg-slate-50'}`}
                          >
                            <td className="py-5 px-8">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${clearedIds.has(tx.id) ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-200 group-hover:border-emerald-400'}`}>
                                {clearedIds.has(tx.id) && <SafeIcon icon={FiCheckCircle} className="text-xs" />}
                              </div>
                            </td>
                            <td className="py-5 px-4 text-xs font-bold text-slate-500">{tx.date}</td>
                            <td className="py-5 px-4">
                              <div className="flex flex-col">
                                <span className="text-[13px] font-black text-slate-900">{tx.payee || tx.description}</span>
                                <span className="text-[9px] text-slate-400 font-bold uppercase">{tx.category || 'General'}</span>
                              </div>
                            </td>
                            <td className={`py-5 px-8 text-right text-[13px] font-black ${tx.total_amount < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                              {formatCurrency(tx.total_amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Right: Statement Summary */}
              <div className="w-full md:w-96 bg-slate-900 text-white p-10 flex flex-col justify-between">
                <div className="space-y-12">
                  <div>
                    <h3 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.3em] mb-8">Reconciliation Summary</h3>
                    
                    <div className="space-y-6">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Statement End</span>
                        <span className="font-black">{formatCurrency(setup.endBalance)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-400 font-bold uppercase tracking-widest text-[10px]">Cleared Balance</span>
                        <span className="font-black text-emerald-400">{formatCurrency(clearedBalance)}</span>
                      </div>
                    </div>

                    <div className={`mt-10 p-8 rounded-[32px] border-2 transition-all ${difference === 0 ? 'bg-emerald-500/10 border-emerald-500' : 'bg-rose-500/10 border-rose-500'}`}>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-2 opacity-70">Difference</p>
                      <p className="text-4xl font-black tracking-tighter">
                        {formatCurrency(difference)}
                      </p>
                      {difference === 0 ? (
                        <div className="flex items-center gap-2 text-emerald-400 text-[10px] font-black uppercase mt-4">
                          <SafeIcon icon={FiCheckCircle} /> Perfectly Reconciled
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-rose-400 text-[10px] font-black uppercase mt-4 animate-pulse">
                          <SafeIcon icon={FiAlertCircle} /> Out of Balance
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
                      <div className="flex items-center gap-3 mb-2">
                        <SafeIcon icon={FiLock} className="text-blue-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest">Audit Lock</span>
                      </div>
                      <p className="text-[10px] text-slate-400 leading-relaxed font-medium">
                        Completing this process will lock {clearedIds.size} transactions. They cannot be edited without reversing this reconciliation.
                      </p>
                    </div>
                  </div>
                </div>

                <button 
                  disabled={difference !== 0 || loading}
                  onClick={handleFinish}
                  className={`w-full py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all shadow-xl flex items-center justify-center gap-2 ${difference === 0 ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-white/10 text-slate-500 cursor-not-allowed'}`}
                >
                  {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Finish Reconciliation'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div className="px-10 py-6 border-t border-slate-200 bg-white flex items-center justify-between">
          <button 
            disabled={loading}
            onClick={() => step === 1 ? onClose() : setStep(1)}
            className="px-8 py-3 rounded-full font-black text-sm text-slate-500 hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50"
          >
            {step === 2 && <SafeIcon icon={FiArrowLeft} />}
            {step === 1 ? 'Cancel' : 'Back to Setup'}
          </button>
          
          {step === 1 && (
            <button 
              disabled={loading || !setup.accountId}
              onClick={startMatching}
              className="px-10 py-3 bg-[#1d4ed8] text-white rounded-full font-black text-sm shadow-xl hover:bg-blue-800 active:scale-95 flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? <SafeIcon icon={FiLoader} className="animate-spin" /> : 'Start Matching'} <SafeIcon icon={FiChevronRight} />
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}